import validation from "../../constants/validation";
import notificationTypes from "../../constants/UIElementNotificationReferences.json";
import notificationFunctionCodes from "../../constants/NotificationFunctionCodes.json";
import { validateElementValue } from "../../components/common/ConstantsBasedTextInput/utils";
import itemsGrouping from "../../constants/grouping/import/items-grouping.json";
import headerGrouping from "../../constants/grouping/import/header-grouping.json";
import {
	SET_DISABLED_DECLARATION_ELEMENTS,
	SET_MANDATORY_DECLARATION_ELEMENTS,
	SET_REQUIRED_PULLDOWN_OPTIONS,
	SET_VALIDATION_ERRORS,
} from "../types";
import headerObligations from "../../constants/obligations/header-obligations";
import itemsObligations from "../../constants/obligations/items-obligations";
import headerOrItemsElements from "../../constants/grouping/import/header-or-items-elements.json";
import { defaultContactElements } from "../../components/layouts/declarations/Sections/ContactForm";
import {
	contactSections,
	headerContactSections,
	itemContactSections,
} from "../../components/common/InputsSection/CustomSections";
import { capitalize, get, trim } from "lodash";
import { getQueryParam, updateQueryParam } from "../../router/utils";
import {
	ELEMENT_QUERY_PARAM,
	ITEM_QUERY_PARAM,
	PAGE_QUERY_PARAM,
	TAB_QUERY_PARAM,
} from "../../constants/GlobalConstants";

/**
 * This validates that the element "unLocode" cannot have a value while any of
 * "deliveryTermsCountryCode" and "deliveryTermsCityCode" have values
 */
const validateUnLocodeElement = (errors, findValueInContext) => {
	const unLocodeValidationElement = validation.find((element) => element.elementName === "unLocode");
	const unLocodeValue = findValueInContext("unLocode");
	const countryCodeValue = findValueInContext("deliveryTermsCountryCode");
	const cityCodeValue = findValueInContext("deliveryTermsCityCode");

	if (unLocodeValidationElement && unLocodeValue) {
		if (countryCodeValue || cityCodeValue) {
			errors.push({
				payload: {
					elements: [unLocodeValidationElement],
				},
				message: "Element cannot have a value while **Delivery Terms Country Code** or **Delivery Terms City Code** also have values",
			});
		}
	} else if (countryCodeValue || cityCodeValue) {
		if (!countryCodeValue) {
			const countryCodeValidationElement = validation.find(
				(element) => element.elementName === "deliveryTermsCountryCode"
			);
			errors.push({
				payload: {
					elements: [countryCodeValidationElement],
				},
				message: `**${countryCodeValidationElement.textDescription}** is required`,
			});
		} else if (!cityCodeValue) {
			const cityCodeValidationElement = validation.find(
				(element) => element.elementName === "deliveryTermsCityCode"
			);
			errors.push({
				payload: {
					elements: [cityCodeValidationElement],
				},
				message: `**${cityCodeValidationElement.textDescription}** is required`,
			});
		}
	}
};

export const getValidationErrorsUtil = (state, dispatch, findValueInContext) => {
	let errors = [];
	const { mandatoryElements, requiredPulldownOptions, declaration } = state;

	const validateElement = (validationElement, elementValue) => {
		if (validationElement.elementName === "unLocode" && elementValue) {
			if (elementValue) {
				validateElementValue(validationElement, elementValue, (warning) => {
					if (warning) {
						errors.push({
							payload: {
								elements: [validationElement],
							},
							message: warning,
						});
					}
				});
			}
		}
	};

	validateUnLocodeElement(errors, findValueInContext);

	/**
	 * Validate the elements in all contact sections
	 * By default, all contact elements are required. However, they will not be required only if:
	 *   - Contact name (contactName) is filled
	 *   - EORI Code (contactEori) is filled
	 */

	const getSectionErrors = (section, itemIndex) => {
		let sectionObject = declaration[section];
		if (typeof itemIndex === "number") {
			if (!declaration.declarationItems) return;
			sectionObject = declaration.declarationItems[itemIndex][section];
		}

		if (
			!sectionObject ||
			!Object.keys(sectionObject).length ||
			(!sectionObject?.contactName && !sectionObject?.contactEori)
		) {
			if (!mandatoryElements.find((element) => element.elementName === section)) {
				return;
			}

			defaultContactElements.forEach((element) => {
				const validationElement = validation.find(
					(valElement) => valElement.elementName === element
				);
				let elementValue = sectionObject && sectionObject[element];
				if (validationElement && !elementValue) {
					errors.push({
						payload: {
							elements: [element],
							itemIndex,
							level: typeof itemIndex === "number" ? "item" : "header",
						},
						message: `**${capitalize(
							section
						)}** - **${validationElement.textDescription
							.replaceAll(": ", "")
							.replaceAll(":", "")}**: Element is required`,
					});
				}
			});
		}
	};

	headerContactSections.forEach((section) => {
		getSectionErrors(section);
	});

	declaration.declarationItems?.forEach((item, index) => {
		itemContactSections.forEach((section) => {
			getSectionErrors(section, index);
		});
	});

	// Validate mandatory elements
	mandatoryElements?.forEach((element) => {
		const validationElement = validation.find((item) => item.elementName === element.elementName);
		if (contactSections.includes(element.elementName)) {
			return;
		}

		// If element is on header level, get the value directly from the declaration object in state
		if (element.level === "header") {
			const elementValue = findValueInContext(element.elementName);
			if ((!elementValue || elementValue?.length === 0) && validationElement) {
				errors.push({
					payload: {
						elements: [validationElement],
						level: "header",
					},
					message: `**${validationElement.textDescription}** is required`,
				});
			} else if (validationElement) {
				validateElement(validationElement, elementValue);
			}
			// If element is on item level, loop through all declaration items and find the element value in each item
		} else if (element.level === "item") {
			declaration.declarationItems?.forEach((item, index) => {
				if (!item[element.elementName]) {
					errors.push({
						payload: {
							elements: [validationElement],
							level: "item",
							itemIndex: index,
						},
						message: `**${validationElement.textDescription}** is required`,
					});
				}
			});
		}
	});

	// Validate multiline elements that do not contain an item with one of the required child element keys
	Object.keys(requiredPulldownOptions).forEach((key) => {
		const { multilineElement, keys, level } = requiredPulldownOptions[key];
		let remainingRequiredKeys = [...keys];

		const getErrorMessage = (validationPulldownElement, remainingRequiredKeys) => {
			return `The element **${validationPulldownElement.textDescription
				.replace(": ", "")
				.replace(
					":",
					""
				)}** requires at least one of the following keys: ${remainingRequiredKeys
				.map((k) => `**${k}**`)
				.join(", ")}`;
		};

		const validateElement = (elementValue, itemIndex) => {
			// Remove the value from the remainingRequiredKeys array if it exists in the multilineElementValue
			elementValue?.forEach((item) => {
				if (remainingRequiredKeys.includes(item[key])) {
					remainingRequiredKeys = remainingRequiredKeys.filter(
						(value) => value !== item[key]
					);
				}
			});

			if (remainingRequiredKeys.length === keys.length) {
				const validationMultilineElement = validation.find(
					(item) => multilineElement === item.elementName
				);
				const validationPulldownElement = validation.find((item) => key === item.elementName);

				if (validationMultilineElement && validationPulldownElement) {
					errors.push({
						payload: {
							elements: [validationMultilineElement],
							level,
							itemIndex,
						},
						message: getErrorMessage(
							validationPulldownElement,
							remainingRequiredKeys
						),
					});
				}
			}
		};

		if (level === "header") {
			let multilineElementValue = findValueInContext(multilineElement);
			validateElement(multilineElementValue);
		} else if (level === "item") {
			declaration.declarationItems?.forEach((item, index) => {
				validateElement(item[multilineElement], index);
			});
		}
	});

	const grouping = getQueryParam(PAGE_QUERY_PARAM) === 1 ? itemsGrouping : headerGrouping;

	grouping.forEach((tab) => {
		tab.sections?.forEach((section) => {
			const findElementInSection = (elements) => {
				elements?.forEach((element) => {
					const elementValue = findValueInContext(element);

					const validationElement = validation.find(
						(item) => item.elementName === element.elementName
					);

					if (
						!errors.find((err) => err.element?.elementName === element) &&
						validationElement &&
						elementValue
					) {
						validateElement(validationElement, elementValue);
					}
					if (element.type === "row") {
						findElementInSection(element.elements);
					}
				});
			};

			findElementInSection(section.elements);
		});
	});

	const headerOrItemValidationErrors = validateHeaderOrItemElementsUtil(findValueInContext);

	dispatch({
		type: SET_VALIDATION_ERRORS,
		payload: [...headerOrItemValidationErrors, ...errors],
	});

	return [...headerOrItemValidationErrors, ...errors];
};

export const findElementObligationsUtil = (state, dispatch, findValueInContext) => {
	let mandatoryElements = [];
	let disabledElements = [];
	// List of mandatory pulldown options for specific multiline elements
	let requiredPulldownOptions = {};

	const obligationFiles = [headerObligations, itemsObligations];

	// Find if the declaration element in rule matches the specified condition
	obligationFiles.forEach((obligations, index) => {
		const level = index === 0 ? "header" : "item";

		const handleMatchedRule = (rule, level, itemIndex) => {
			if (rule.includes(".isMandatory")) {
				mandatoryElements.push({
					elementName: rule.replace(".isMandatory", ""),
					level,
					itemIndex,
				});
			} else if (rule.includes(".isDisabled")) {
				disabledElements.push({
					elementName: rule.replace(".isDisabled", ""),
					level,
					itemIndex,
				});
			} else if (rule.includes(".mustHave")) {
				const multilineElement = rule.split(".")[0];
				const pulldownElement = rule.split(".")[1];
				const requiredKeys = rule.split(".")[3].split(",");
				requiredPulldownOptions[pulldownElement] = {
					multilineElement,
					level,
					itemIndex,
					keys: requiredKeys,
				};
			}
		};

		obligations.rules.forEach((rule) => {
			let matches = false;
			let matchingItems = [];
			rule.if.forEach((condition) => {
				Object.keys(condition).forEach((key) => {
					if (level === "item") {
						state?.declaration?.declarationItems?.forEach((item, index) => {
							if (
								condition[key].includes(
									findValueInContext(key) || item[key]
								)
							) {
								if (!matchingItems.includes(index)) {
									matchingItems.push(index);
								}
							}
						});
					} else {
						if (condition[key].includes(findValueInContext(key))) {
							matches = true;
						}
					}
				});
			});

			// If condition matches, mark all elements that end with .isMandatory as required/mandatory elements
			// and elements that end with .isDisabled as disabled
			if (matches && level === "header") {
				rule.then.forEach((rule) => handleMatchedRule(rule, "header"));
			}

			if (level === "item") {
				matchingItems.forEach((index) => {
					rule.then.forEach((rule) => handleMatchedRule(rule, "item", index));
				});
			}
		});

		// If a group element has a value, make the rest of the group elements mandatory.
		obligations.groups.forEach((group) => {
			let groupHasValue = false;
			group.forEach((element) => {
				if (findValueInContext(element)) {
					groupHasValue = true;
				}
			});

			if (groupHasValue) {
				mandatoryElements = [
					...mandatoryElements,
					...group.map((element) => ({ elementName: element, level })),
				];
			}
		});

		const serviceObject = obligations.services[state.declaration.service] || {};

		// Mark elements in the currently selected declaration service obligation as mandatory
		Object.keys(serviceObject).forEach((key) => {
			const value = serviceObject[key];

			if (!value) {
				mandatoryElements.push({ elementName: key, level });
			}
		});
	});

	dispatch({
		type: SET_MANDATORY_DECLARATION_ELEMENTS,
		payload: mandatoryElements,
	});

	dispatch({
		type: SET_DISABLED_DECLARATION_ELEMENTS,
		payload: disabledElements,
	});

	dispatch({
		type: SET_REQUIRED_PULLDOWN_OPTIONS,
		payload: requiredPulldownOptions,
	});
};

/**
 * Each object in the "uiElements" array that's returned from the back-end consists of a "validationCode"
 * which should match the "referenceId" of one of the entries in the "UIElementNotificationReferences.json" file,
 * and "tagIDJsonPath" which is an array of elements which will be used to fill the {{element}} placeholders in the "message"
 * property of the array items in "UIElementNotificationReferences.json"
 */
export const parseUiElementNotifications = (uiElements) => {
	let parsedNotifications = [];

	uiElements.forEach((notification) => {
		if (!Array.isArray(notification) && typeof notification === "object") {
			let error = {
				level: "header",
				elements: [],
			};

			notification.tagIDJsonPath?.forEach((path) => {
				const splitPath = path.split(".");

				splitPath.forEach((element, index) => {
					if (element.includes("declarationItems")) {
						error = {
							...error,
							level: "item",
							itemIndex:
								parseInt(element?.split("[")[1]?.split("]")[0] || 0) -
								1,
						};
					} else if (index + 1 === splitPath.length) {
						const validationElement = validation.find(
							({ elementName }) => elementName === element
						);
						error.elements.push(validationElement);
					}
				});
			});
			const notificationType = notificationTypes.find(
				(type) => type.referenceId === notification.validationCode
			);

			if (notificationType) {
				parsedNotifications.push({
					type: "validationError",
					message: notificationType.message
						.replace(
							"{{element}}",
							`**${trim(
								error.elements[0]?.textDescription.replaceAll(":", "")
							)}**`
						)
						.replace(
							"{{element1}}",
							`**${trim(
								error.elements[1]?.textDescription.replaceAll(":", "")
							)}**`
						)
						.replace(
							"{{element2}}",
							`**${trim(
								error.elements[2]?.textDescription.replaceAll(":", "")
							)}**`
						),
					payload: error,
					processedTime: notification.processedTime,
				});
			}
		}
	});

	return parsedNotifications;
};

/** Takes a notification object that's returned from the API, generate the notification message
 * using the "functionCode" from the "NotificationFunctionCodes.json" file, and adds the "displayElements" will be displayed
 * when the "View" button next to the notification is clicked
 */
export const parseNotificationObject = (notification) => {
	let result = {
		processedTime: notification.processedTime,
	};

	const variablesRegex = /{{(.*)}}/g;

	const notificationInfo = notificationFunctionCodes[notification.functionCode];

	if (notificationInfo?.message) {
		let message = "";

		const variables = notificationInfo.message.match(variablesRegex);
		if (variables?.length) {
			variables.forEach((variable) => {
				variable = variable.replace("{{", "").replace("}}", "");
				let value = "";
				if (variable === "status" && notification.functionCode === 11) {
					value = "accepted";
				} else {
					value = "denied";
				}

				message = notificationInfo.message.replace(`{{${variable}}}`, value);
			});
		} else {
			message = notificationInfo.message;
		}

		result = { ...result, ...notificationInfo, message };
	}

	/** Elements that exist on all notification that wouldn't be of interest to the user */
	let excludedElements = ["declarationId", "functionalReferenceIdOfDeclaration", "versionId"];

	/** Display elements are elements that will be displayed to the user when the "view" button
	 * next to the notification is clicked. Only string, number or array elements are extracted from the
	 * "declaration" object
	 */
	const getDisplayElementsFromObject = (object) => {
		Object.keys(object).forEach((key) => {
			const value = object[key];
			const isArrayOfObjects = Array.isArray(value) && value.length && typeof value[0] === "object";

			if (
				(isArrayOfObjects || typeof value === "string" || typeof value === "number") &&
				!excludedElements.includes(key)
			) {
				result.elements = { ...(result.elements || {}), [key]: value };
			} else if (typeof value === "object") {
				getDisplayElementsFromObject(value);
			}
		});
	};

	if (notification.declaration) {
		getDisplayElementsFromObject(notification.declaration);
	}

	return result;
};

export const handleValidationErrorsView = (error) => {
	const { elements } = error.payload;
	const elementName = elements.find((element) => element?.elementName)?.elementName;
	if (!elementName) {
		return;
	}
	updateQueryParam(ELEMENT_QUERY_PARAM, elementName);

	if (typeof error.payload.itemIndex === "number") {
		updateQueryParam(ITEM_QUERY_PARAM, error.payload.itemIndex);
	}

	updateQueryParam(TAB_QUERY_PARAM, error.payload.level === "item" ? 1 : 0);
};

/** Header or Items elements should only have a value either on header or item level,
 * this method validates this rule and shows errors in the notifications panel if there are any
 */
export const validateHeaderOrItemElementsUtil = (findValueInContext) => {
	let errors = [];

	headerOrItemsElements.forEach((element) => {
		const isSection = element.includes("section-");
		const elementOrSectionName = isSection ? element.split("section-")[1] : element;

		const headerValue = findValueInContext(elementOrSectionName, "header");
		const itemValue = findValueInContext(elementOrSectionName, "item");

		if (headerValue && itemValue) {
			// Return if the value is of type array and one of the header or item arrays are empty
			if (Array.isArray(headerValue) && Array.isArray(itemValue)) {
				if (!headerValue.length || !itemValue.length) {
					return;
				}
			} else if (typeof headerValue === "object" && typeof itemValue === "object") {
				if (
					!Object.values(headerValue).filter((value) => value).length ||
					!Object.values(itemValue).filter((value) => value).length
				) {
					return;
				}
			}
			const validationElement = validation.find(({ elementName }) =>
				isSection
					? elementName === Object.keys(headerValue)[0]
					: elementName === elementOrSectionName
			);

			errors.push({
				message: `The ${element.startsWith("section") ? "section" : "element"} **${
					element === "buyer" ? "Contact" : ""
				}${
					!isSection
						? validationElement.textDescription.replaceAll(":", "")
						: capitalize(elementOrSectionName)
				}** has a value on both header and item levels. It is allowed to have a value on only one of them`,
				payload: {
					level: "header",
					elements: [validationElement],
				},
			});
		}
	});

	return errors;
};
